// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the dataset on the terrain (owner, 2026-10-18; branch
 * feat/city-data-drape). The earth layer draws a dataset on its own sphere,
 * at sea level close in (or lifted above Everest further out), and without a
 * depth test, so with the terrain on it floats above the ground or slides
 * off hills when tilted. Close over a city pack with the terrain on, the
 * visible part of the dataset is instead drawn as a flat Web Mercator picture
 * (`requestDrape`, the dataset shader's `uMercMode`) and handed to MapLibre as
 * a canvas source, which drapes it on its terrain like the aerial photos:
 * at the ground's height, hidden behind hills. The globe leaves that box to
 * it (`setDrapeHole`) and still draws the rest, far out at a steep tilt.
 *
 * Only the dataset is draped; the real-time overlays stay on the sphere.
 */

import type { CanvasSource, Map as MaplibreMap } from 'maplibre-gl'
import type { GeoRect } from './earthTileLayer'

/** A box of the flat Web Mercator picture: longitudes in degrees, Mercator y in 0..1 (top < bottom). */
export interface DrapeBox {
  west: number
  east: number
  top: number
  bottom: number
}

/**
 * From this zoom, with the terrain on, the dataset is draped over the city pack's terrain: at every
 * zoom since 2026-10-18 (owner: it floated further out), the box cut to the pack, the only place the
 * ground has height. Far out the pack is small on screen, and so is the picture.
 */
export const DRAPE_FROM_ZOOM = 0

/** The view's box is widened by this share of its size on each side, so a pan stays covered until the next drape. */
export const DRAPE_MARGIN = 0.25

/** At most this many view widths across (a steep tilt sees to the horizon; the globe draws what lies beyond). */
export const DRAPE_MAX_VIEWS = 4

const MAX_LAT = 85.051129

/** Web Mercator y (0 at the north edge, 1 at the south) of a latitude. */
export function mercatorY(lat: number): number {
  const l = Math.max(-MAX_LAT, Math.min(MAX_LAT, lat)) * Math.PI / 180
  return (1 - Math.log(Math.tan(Math.PI / 4 + l / 2)) / Math.PI) / 2
}

/** The latitude of a Web Mercator y. */
export function latOfMercatorY(y: number): number {
  return Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180 / Math.PI
}

/** What to drape for a view: the box, its picture size, and the hole the globe leaves for it (degrees). */
export interface DrapePlan {
  box: DrapeBox
  width: number
  height: number
  hole: GeoRect
}

/**
 * The box to drape for a view: its bounds widened by {@link DRAPE_MARGIN},
 * at most {@link DRAPE_MAX_VIEWS} view widths around the centre, cut to a
 * regional dataset's box and to the terrain's (the city pack's; outside it the ground is flat).
 * A view across the antimeridian (the whole globe, far out) counts as the whole world. About one picture pixel to a screen pixel at the
 * centre, at most `maxPixels` on a side. Null when nothing is left.
 */
export function drapePlanFor(
  view: { west: number; east: number; north: number; south: number },
  center: { lng: number; lat: number },
  zoom: number,
  viewport: { width: number; height: number },
  maxPixels: number,
  dataset?: GeoRect | null,
  terrain?: GeoRect | null,
): DrapePlan | null {
  if (!(view.west < view.east)) view = { ...view, west: -180, east: 180 }
  const worldSize = 512 * 2 ** zoom
  const top0 = mercatorY(view.north)
  const bottom0 = mercatorY(view.south)
  const dx = (view.east - view.west) * DRAPE_MARGIN
  const dy = (bottom0 - top0) * DRAPE_MARGIN
  let west = view.west - dx
  let east = view.east + dx
  let top = top0 - dy
  let bottom = bottom0 + dy
  // Not more than a few views around the centre.
  const span = DRAPE_MAX_VIEWS * Math.max(viewport.width, viewport.height) / worldSize
  const cx = (center.lng + 180) / 360
  const cy = mercatorY(center.lat)
  west = Math.max(west, (cx - span / 2) * 360 - 180)
  east = Math.min(east, (cx + span / 2) * 360 - 180)
  top = Math.max(top, cy - span / 2, mercatorY(MAX_LAT))
  bottom = Math.min(bottom, cy + span / 2, mercatorY(-MAX_LAT))
  for (const cut of [dataset, terrain]) {
    if (!cut || !(cut.w <= cut.e)) continue
    west = Math.max(west, cut.w)
    east = Math.min(east, cut.e)
    top = Math.max(top, mercatorY(cut.n))
    bottom = Math.min(bottom, mercatorY(cut.s))
  }
  if (!(west < east) || !(top < bottom)) return null
  let width = (east - west) / 360 * worldSize
  let height = (bottom - top) * worldSize
  const scale = Math.min(1, maxPixels / Math.max(width, height))
  width = Math.max(1, Math.round(width * scale))
  height = Math.max(1, Math.round(height * scale))
  return {
    box: { west, east, top, bottom },
    width,
    height,
    hole: { n: latOfMercatorY(top), s: latOfMercatorY(bottom), w: west, e: east },
  }
}

/** The drape's MapLibre source and layer. */
export const DRAPE_SOURCE = 'dataset-drape'
export const DRAPE_LAYER = 'dataset-drape-layer'

/** What the drape needs from the map renderer and its earth layer. */
export interface DrapeHost {
  /** Whether to drape now: the city close-up with MapLibre's sphere corrected, the terrain drawn, on the Earth. */
  wanted(): boolean
  requestDrape(box: DrapeBox, width: number, height: number): Promise<Uint8Array | null>
  setDrapeHole(hole: GeoRect | null): void
  /** The frame in the dataset texture (a playing stream moves it), or null. */
  frameTime(): number | null
  /** A regional dataset's box, or null for a global one. */
  datasetBox(): GeoRect | null
  /** The box the terrain covers (the city pack's), or null. */
  terrainBox(): GeoRect | null
  /** The longest side of the picture, in pixels. */
  maxPixels(): number
  /** The layer the drape goes under (the first above the city photos), or undefined for the top. */
  beforeLayer(): string | undefined
}

/** A redraw at most this often while a stream plays (ms). */
const FRAME_INTERVAL = 100
/** And this often after other changes (palette, opacity, a new dataset), seen as renders not our own (ms). */
const CHANGE_INTERVAL = 600

/**
 * Keep the dataset draped on MapLibre's terrain while `host.wanted()`:
 * redrawn when the camera stops, when the view leaves the draped box, when
 * a stream's frame changes, and shortly after anything else repaints the
 * globe (a palette, an opacity, a new dataset). Returns a function that
 * takes it off.
 */
export function watchDataDrape(map: MaplibreMap, host: DrapeHost): () => void {
  let canvas: HTMLCanvasElement | null = null
  let plan: DrapePlan | null = null
  let busy = false
  let again = false
  let lastAt = 0
  let lastFrame: number | null = null
  let rendersSince = 0

  const off = () => {
    if (map.getLayer(DRAPE_LAYER)) map.removeLayer(DRAPE_LAYER)
    if (map.getSource(DRAPE_SOURCE)) map.removeSource(DRAPE_SOURCE)
    host.setDrapeHole(null)
    plan = null
  }

  const redraw = async (): Promise<void> => {
    if (busy) {
      again = true
      return
    }
    if (!host.wanted() || map.getZoom() < DRAPE_FROM_ZOOM) {
      if (plan) off()
      return
    }
    const b = map.getBounds()
    const el = map.getCanvas()
    const next = drapePlanFor(
      { west: b.getWest(), east: b.getEast(), north: b.getNorth(), south: b.getSouth() },
      map.getCenter(), map.getZoom(), { width: el.clientWidth, height: el.clientHeight }, host.maxPixels(), host.datasetBox(), host.terrainBox(),
    )
    if (!next) {
      if (plan) off()
      return
    }
    busy = true
    const frame = host.frameTime()
    let rows: Uint8Array | null = null
    try {
      rows = await host.requestDrape(next.box, next.width, next.height)
    } finally {
      busy = false
    }
    lastAt = performance.now()
    lastFrame = frame
    rendersSince = 0
    if (!rows || !host.wanted()) {
      if (plan) off()
    } else {
      canvas ??= document.createElement('canvas')
      if (canvas.width !== next.width) canvas.width = next.width
      if (canvas.height !== next.height) canvas.height = next.height
      canvas.getContext('2d')?.putImageData(new ImageData(new Uint8ClampedArray(rows.buffer as ArrayBuffer, rows.byteOffset, rows.byteLength), next.width, next.height), 0, 0)
      const { west: w, east: e } = next.box
      const n = next.hole.n
      const s = next.hole.s
      const coordinates: [[number, number], [number, number], [number, number], [number, number]] = [[w, n], [e, n], [e, s], [w, s]]
      const source = map.getSource(DRAPE_SOURCE) as CanvasSource | undefined
      if (!source) {
        map.addSource(DRAPE_SOURCE, { type: 'canvas', canvas, coordinates, animate: false })
        map.addLayer({ id: DRAPE_LAYER, type: 'raster', source: DRAPE_SOURCE, paint: { 'raster-fade-duration': 0 } }, host.beforeLayer())
      } else {
        source.setCoordinates(coordinates)
        // A still canvas is read once: play for one frame to take the new picture.
        source.play()
        map.once('render', () => source.pause())
      }
      host.setDrapeHole(next.hole)
      plan = next
    }
    if (again) {
      again = false
      void redraw()
    }
  }

  /** Whether the view has left the draped box (by more than half the margin). */
  const viewLeft = (): boolean => {
    if (!plan) return true
    const b = map.getBounds()
    return b.getWest() < plan.box.west || b.getEast() > plan.box.east
      || mercatorY(b.getNorth()) < plan.box.top || mercatorY(b.getSouth()) > plan.box.bottom
  }

  const onMoveEnd = () => { void redraw() }
  const onRender = () => {
    rendersSince++
    if (busy) return
    const now = performance.now()
    const frame = host.frameTime()
    if (frame !== lastFrame && now - lastAt >= FRAME_INTERVAL) void redraw()
    // Our own drape costs two renders (the pass, then the canvas upload); more are someone else's change.
    else if (rendersSince > 2 && now - lastAt >= CHANGE_INTERVAL && (host.wanted() || plan)) void redraw()
  }
  const onMove = () => {
    if (plan && !busy && performance.now() - lastAt >= 150 && viewLeft()) void redraw()
  }
  map.on('moveend', onMoveEnd)
  map.on('render', onRender)
  map.on('move', onMove)
  void redraw()
  return () => {
    map.off('moveend', onMoveEnd)
    map.off('render', onRender)
    map.off('move', onMove)
    off()
  }
}

/** False when the page was opened with `?cityDrape=0`: the dataset then stays on the globe's sphere close in. */
export function isDataDrape(search: string = globalThis.location?.search ?? ''): boolean {
  try {
    return new URLSearchParams(search).get('cityDrape') !== '0'
  } catch {
    return true
  }
}
